import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { fetchMenuCategories, fetchMenuItems } from "@/lib/api";
import SectionHeader from "@/components/SectionHeader";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Flame, Search } from "lucide-react";
import { Input } from "@/components/ui/input";

export default function MenuPage() {
    const [categories, setCategories] = useState([]);
    const [items, setItems] = useState([]);
    const [active, setActive] = useState("all");
    const [q, setQ] = useState("");

    useEffect(() => {
        Promise.all([fetchMenuCategories(), fetchMenuItems()]).then(([c, i]) => {
            setCategories(c);
            setItems(i);
        });
    }, []);

    const grouped = useMemo(() => {
        const map = {};
        const filtered = items.filter((it) =>
            !q || it.name.toLowerCase().includes(q.toLowerCase()) || (it.description || "").toLowerCase().includes(q.toLowerCase())
        );
        for (const it of filtered) {
            map[it.category] = map[it.category] || [];
            map[it.category].push(it);
        }
        return map;
    }, [items, q]);

    const visibleCats = active === "all" ? categories : categories.filter((c) => c.slug === active);

    return (
        <div data-testid="menu-page">
            <section className="section">
                <div className="container-narrow">
                    <SectionHeader
                        eyebrow="The Menu"
                        title="Slow smoke. Bold flavor. Real plates."
                        subtitle="All meats wood-smoked on site every day. Prices and items rotate — daily specials marked with a flame."
                    />

                    {/* Search */}
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
                        <div className="relative w-full sm:max-w-xs">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50" size={16} />
                            <Input
                                placeholder="Search menu — brisket, tacos, sides..."
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                className="pl-9 bg-black/40 border-white/10 focus:border-orange-400/50 placeholder:text-white/40"
                                data-testid="menu-search-input"
                            />
                        </div>
                    </div>

                    {/* Category Tabs */}
                    <Tabs value={active} onValueChange={setActive} className="w-full" data-testid="menu-category-tabs">
                        <TabsList className="flex flex-wrap justify-start h-auto bg-black/40 border border-white/10 p-1 rounded-md gap-1">
                            <TabsTrigger value="all" className="data-[state=active]:bg-orange-500/20 data-[state=active]:text-orange-300 text-white/70 px-3 py-1.5 text-xs uppercase tracking-[0.18em] font-display rounded" data-testid="menu-tab-all">All</TabsTrigger>
                            {categories.map((c) => (
                                <TabsTrigger key={c.slug} value={c.slug} data-testid={`menu-tab-${c.slug}`} className="data-[state=active]:bg-orange-500/20 data-[state=active]:text-orange-300 text-white/70 px-3 py-1.5 text-xs uppercase tracking-[0.18em] font-display rounded">
                                    {c.label}
                                </TabsTrigger>
                            ))}
                        </TabsList>

                        <TabsContent value={active} className="mt-8">
                            <div className="space-y-12">
                                <AnimatePresence>
                                    {visibleCats.map((cat) => {
                                        const list = grouped[cat.slug] || [];
                                        if (list.length === 0) return null;
                                        return (
                                            <motion.div
                                                key={cat.slug}
                                                initial={{ opacity: 0, y: 12 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0 }}
                                                transition={{ duration: 0.35 }}
                                                data-testid={`menu-category-block-${cat.slug}`}
                                            >
                                                <div className="flex items-end justify-between mb-4">
                                                    <div>
                                                        <div className="text-[11px] uppercase tracking-[0.32em] text-orange-300/90">Section</div>
                                                        <h3 className="font-display text-3xl tracking-[0.05em] text-white">{cat.label}</h3>
                                                        {cat.description && <p className="text-white/60 text-sm mt-1">{cat.description}</p>}
                                                    </div>
                                                </div>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    {list.map((it) => (
                                                        <Card key={it.id} className="surface-card border-0" data-testid="menu-item-card">
                                                            <CardContent className="p-5">
                                                                <div className="flex items-start justify-between gap-3">
                                                                    <div className="flex-1">
                                                                        <div className="flex items-center gap-2 flex-wrap">
                                                                            <div className="font-display text-lg tracking-[0.04em] text-white" data-testid="menu-item-name">{it.name}</div>
                                                                            {it.is_special && (
                                                                                <Badge className="bg-orange-400/15 text-orange-300 border-orange-400/30" variant="outline">
                                                                                    <Flame size={12} className="mr-1" /> Special
                                                                                </Badge>
                                                                            )}
                                                                        </div>
                                                                        {it.description && (
                                                                            <p className="text-white/70 text-sm mt-1 leading-relaxed" data-testid="menu-item-description">{it.description}</p>
                                                                        )}
                                                                    </div>
                                                                    <div className="text-orange-300 font-display text-lg whitespace-nowrap" data-testid="menu-item-price">{it.price}</div>
                                                                </div>
                                                            </CardContent>
                                                        </Card>
                                                    ))}
                                                </div>
                                            </motion.div>
                                        );
                                    })}
                                </AnimatePresence>
                                {Object.keys(grouped).length === 0 && (
                                    <div className="text-white/60" data-testid="menu-empty-state">No items found.</div>
                                )}
                            </div>
                        </TabsContent>
                    </Tabs>
                </div>
            </section>
        </div>
    );
}
